import { formatDate } from "@/modules/carga-trabalho";
import type { CargaTrabalhoIntervalo } from "@/modules/carga-trabalho";
import { textoDaObra } from "@/shared/utils/formatar-identificacao-obra";
import type {
  AcaoSimulada,
  AlteracaoSimulada,
  ConflitoNovo,
  ObraSimulada,
} from "./simulacao.model";
import type { EstadoAplicacao } from "./simulacao.aplicacao";
import type { CategoriaAlteracao } from "./simulacao.resumo";

/* Textos do Modo Simulação. */

export const SIMULACAO_MARCA = "Alterado na simulação";
/** Botão da Revisão do Remanejar (Figma 10745:112115). */
export const SIMULACAO_APLICAR_LABEL = "Aplicar na simulação";
/** Botão da escolha "Ignorar Conflito" (Figma 9497:109954, modal de baixo): com S maiúsculo. */
export const SIMULACAO_APLICAR_IGNORAR_LABEL = "Aplicar na Simulação";
/** Erro de validação (Figma 10121:46353): o Toast sai com severidade `error`. */
export const SIMULACAO_NADA_ALTERADO =
  "Nada foi alterado. Mude um campo para incluir na simulação.";
/** Falha ao gravar a instrução na simulação: a alteração não entra no cenário. */
export const MENSAGEM_INSTRUCAO_RECUSADA = "Não foi possível incluir a alteração na simulação.";

/** Texto do erro no modal: o do serviço; sem ele, o padrão; só "inválido", com o contexto na frente. */
export function mensagemInstrucaoRecusada(mensagemDoServico: string): string {
  const mensagem = mensagemDoServico.trim();
  if (!mensagem) return MENSAGEM_INSTRUCAO_RECUSADA;
  return /^inv[aá]lid[oa]\.?$/i.test(mensagem)
    ? `Não foi possível incluir na simulação: ${mensagem}`
    : mensagem;
}
/** Falha do DELETE da instrução: o item permanece no cenário. */
export const TOAST_SIMULACAO_ATIVA_FALHOU = "Não foi possível carregar a simulação em andamento.";
export const TOAST_SIMULACAO_ATIVA_FALHOU_DETALHE = "Tente novamente.";
export const TOAST_DESFAZER_FALHOU = "Não foi possível desfazer a alteração.";
export const SIMULACAO_CONFLITO_RESOLVIDO = "Conflito resolvido na simulação.";
export const SIMULACAO_CONFLITO_TRATADO = "Conflito marcado como tratado na simulação.";

/* Faixa do Modo Simulação (Figma 9170:48640, 9724:44301, 9725:44279). */
export const SIMULACAO_TITULO = "Modo Simulação";
/** 0 itens: rótulo Figma; o clique segue `POST .../descarte`. */
export const SIMULACAO_SAIR_LABEL = "Sair da simulação";
export const SIMULACAO_AVISO = "Nada é gravado até você aplicar.";
/** Com alterações, a faixa troca o aviso pelo estado do cenário. */
export const SIMULACAO_AVISO_COM_ALTERACOES = "Nada foi gravado ainda.";
/** Aviso de divergências detectadas pela API ao carregar a simulação ativa. */
export const SIMULACAO_AVISO_DIVERGENCIAS =
  "Algumas alterações podem ter sido afetadas por mudanças nos dados. Verifique antes de aplicar.";
export const SIMULACAO_COMPARAR_LABEL = "Comparar";
/** Badge do tooltip da barra/selo no Gantt (Figma 10892:402 / QA 3149). */
export const SIMULACAO_TIP_BADGE = "Simulação";
/** Mobile do Comparar (Figma 9889:86692): o mesmo botão, texto curto. */
export const INDICADORES_LABEL = "Indicadores";
/** Mobile: o botão primário da faixa vira "Aplicar". */
export const SIMULACAO_APLICAR_CURTO = "Aplicar";

/** "Aplicar Simulação" sem alterações; "Aplicar Simulação (N)" com elas. */
export function rotuloAplicarSimulacao(alteracoes: number): string {
  return alteracoes === 0 ? "Aplicar Simulação" : `Aplicar Simulação (${alteracoes})`;
}

/** "0 alterações", "1 alteração", "2 alterações". */
export function formatAlteracoes(total: number): string {
  return `${total} ${total === 1 ? "alteração" : "alterações"}`;
}

/** Toast de sucesso ao incluir a alteração no cenário. */
export function mensagemAlteracaoIncluida(alteracao: AlteracaoSimulada): string {
  switch (alteracao.acao) {
    case "definir-equipe":
      return `Equipe da Obra ${alteracao.obra.codigo} alterada na simulação.`;
    case "remanejar-profissional":
    case "remanejar-fornecedor":
    case "remanejar-local":
      return SIMULACAO_CONFLITO_RESOLVIDO;
    case "ignorar-conflito-profissional":
    case "ignorar-conflito-fornecedor":
    case "ignorar-conflito-local":
      return SIMULACAO_CONFLITO_TRATADO;
  }
}

/** 2ª linha do toast de inclusão (Figma 10121:46159): quem entra no lugar de quem. */
export function detalheAlteracaoIncluida(alteracao: AlteracaoSimulada): string | undefined {
  switch (alteracao.acao) {
    case "remanejar-profissional":
    case "remanejar-fornecedor":
    case "remanejar-local":
      return `${alteracao.novo.nome} substitui ${alteracao.anterior.nome}.`;
    default:
      return undefined;
  }
}

/** "Alteração incluída com 1 novo conflito." / "... com 2 novos conflitos." */
export function mensagemNovosConflitos(total: number): string {
  return `Alteração incluída com ${total} ${total === 1 ? "novo conflito" : "novos conflitos"}.`;
}

/** Segunda linha do aviso (Figma 10121:46299): "Revise Lucas Souza antes de aplicar." */
export function detalheNovosConflitos(conflitos: Pick<ConflitoNovo, "recurso">[]): string {
  const recursos = [...new Set(conflitos.map((conflito) => conflito.recurso))];
  return `Revise ${recursos.join(", ")} antes de aplicar.`;
}

/** Tooltip da marca na linha de quem sai de uma obra (remanejamento ou nova equipe). */
export function descreverSaida(obra: Pick<ObraSimulada, "codigo">): string {
  return `Removido da Obra ${obra.codigo} na simulação`;
}

/** Valor anterior, para o tooltip da marca "Alterado na simulação". */
export function descreverAnterior(alteracao: AlteracaoSimulada): string {
  switch (alteracao.acao) {
    case "definir-equipe":
      return alteracao.anterior.length > 0
        ? `Equipe anterior: ${alteracao.anterior.map((membro) => membro.nome).join(", ")}`
        : `Equipe anterior: ${SEM_EQUIPE}`;
    case "remanejar-profissional":
      return `Profissional anterior: ${alteracao.anterior.nome}`;
    case "remanejar-fornecedor":
      return `Fornecedor anterior: ${alteracao.anterior.nome}`;
    case "remanejar-local":
      return `Local anterior: ${alteracao.anterior.nome}`;
    case "ignorar-conflito-profissional":
    case "ignorar-conflito-fornecedor":
    case "ignorar-conflito-local":
      return `${CONFLITO_EM_ABERTO}: ${alteracao.obras.map((obra) => `Obra ${obra.codigo}`).join(" × ")}`;
  }
}

/* Rótulos de Comparar e Aplicar Simulação. */

export const CATEGORIA_LABEL: Record<CategoriaAlteracao, string> = {
  profissional: "Profissional",
  fornecedor: "Fornecedor",
  local: "Local",
};

export const TITULO_ACAO: Record<AcaoSimulada, string> = {
  "definir-equipe": "Definir equipe",
  "remanejar-profissional": "Remanejar profissional",
  "remanejar-fornecedor": "Remanejar fornecedor",
  "remanejar-local": "Remanejar local",
  "ignorar-conflito-profissional": "Ignorar conflito",
  "ignorar-conflito-fornecedor": "Ignorar conflito",
  "ignorar-conflito-local": "Ignorar conflito",
};

/** Campos dos cartões, na ordem de exibição. */
export const ROTULO_CAMPO = {
  profissional: "Profissional",
  obra: "Obra",
  papel: "Papel",
  escopoMacro: "Escopo Macro",
  inicio: "Início",
  fim: "Fim",
  alocacao: "Alocação",
} as const;

export const NAO_INFORMADO = "—";
export const ROTULO_REMOVIDO = "Removido";
export const ROTULO_PLANEJAMENTO = "Planejamento";

export const ROTULO_FECHAMENTO = "Fechamento";
export const SEM_EQUIPE = "Sem equipe";
export const CONFLITO_EM_ABERTO = "Conflito em aberto";
export const CONFLITO_TRATADO = "Conflito tratado";

/** Cartão da remoção vinculada: para onde vai quem saiu. */
export const LIBERADO: Record<CategoriaAlteracao, string> = {
  profissional: "Liberado da obra",
  fornecedor: "Liberado do escopo",
  local: "Liberado da obra",
};

/** Comparar: a versão atual sem o recurso (ex.: obra sem equipe). */
export const SEM_RECURSO: Record<CategoriaAlteracao, string> = {
  profissional: "Sem alocação",
  fornecedor: "Sem fornecedor no escopo",
  local: "Sem local",
};

export const MENSAGEM_ARQUIVAR = "A simulação sai da tela e fica no histórico.";
/** Título da confirmação de Sair da simulação com alterações. */
export const TITULO_SAIR_SIMULACAO = "Sair da simulação?";

/** "Obra 24 - Austral Abrolhos" (ADR 0018). */
export function rotuloObra(obra: Pick<ObraSimulada, "codigo" | "nome" | "embarcacao">): string {
  return `Obra ${textoDaObra(obra)}`;
}

/** "01/02/2026 a 30/06/2026". */
export function formatarPeriodo(intervalo: CargaTrabalhoIntervalo): string {
  return `${formatDate(intervalo.inicio)} a ${formatDate(intervalo.fim)}`;
}

/**
 * Textos de Comparar e Aplicar. As mensagens MS001–MS011 e os rótulos são literais
 * do produto; os textos de estado dos itens não têm literal e estão marcados como tal.
 */

function alteracoes(total: number): string {
  return `${total} ${total === 1 ? "alteração" : "alterações"}`;
}

/** Mensagens literais do produto. */
export const MS = {
  /** Comparar: texto explicativo do modal. */
  MS001: "Todas as alterações da simulação, agrupadas por obra. Nada foi gravado ainda.",
  /** Aplicar: texto explicativo do modal. */
  MS002: "Confira o que vai mudar e registre o motivo. Nada é gravado até você confirmar.",
  /** Aplicar: aviso no painel Resumo. */
  MS003: "Ao confirmar, as alterações são gravadas e o Modo Simulação é encerrado.",
  /** Descartar: título. */
  MS004_TITULO: "Descartar simulação?",
  /** Junto ao campo Justificativa (o campo já o exibe). */
  MS007: "(Campo obrigatório)",
  /** Durante a aplicação. */
  MS008:
    "Gravando as alterações no sistema. Se alguma falhar, nenhuma entra. Não feche esta janela.",
  /** Falha do sistema. */
  MS009: "Houve uma falha do sistema. O que não foi gravado pode ser enviado de novo.",
  /** Item descartado: profissional removido da obra (RN020). */
  MS010: "Um profissional foi removido da obra depois que a simulação começou.",
  /** Item descartado: escopo macro transferido (RN023). */
  MS011: "O escopo macro foi transferido para outra obra depois que a simulação começou.",
} as const;

/** Aviso do Resumo no Aplicar mobile (Figma 9699:112791): o desktop usa `MS.MS003` (9651:41026). */
export const MS003_MOBILE =
  "Ao confirmar, as alterações são gravadas no sistema e o modo simulação é encerrado.";

/** Confirmação do Desfazer alteração (Figma 10896:56193). */
export const DESFAZER_TITULO = "Desfazer alteração?";
export const DESFAZER_MENSAGEM =
  "A alteração volta para o cronograma atual e sai da simulação. Se fizer parte de uma substituição, a remoção ligada a ela também é desfeita.";

/** MS004 — Descartar: "As N alterações feitas nesta simulação serão perdidas. ..." */
export function mensagemDescartar(total: number): string {
  return total === 1
    ? "A 1 alteração feita nesta simulação será perdida. Nada foi gravado no sistema."
    : `As ${alteracoes(total)} feitas nesta simulação serão perdidas. Nada foi gravado no sistema.`;
}

/** MS006 — cartão da remoção vinculada a uma substituição. */
export function mensagemRemocaoVinculada(substituto: string): string {
  return `Remoção vinculada à substituição acima. Para desfazer, restaure a alteração de ${substituto}.`;
}

/** Chip entre o cartão de quem entra e o de quem sai (Figma 9651:41026). */
export function vinculoSubstituicao(novo: string, anterior: string): string {
  return `Substituição · ${novo} entra no lugar de ${anterior}`;
}

/** Tag do cartão e aviso do Resumo: quem continua com conflito após aplicar (Figma 9651:41026). */
export function tagConflitos(total: number): string {
  return `${total} ${total === 1 ? "Conflito" : "Conflitos"}`;
}

export function avisoConflitoResidual(nome: string, total: number): string {
  return total === 1
    ? `${nome} continua com 1 conflito após aplicar. Ele segue sinalizado no cronograma.`
    : `${nome} continua com ${total} conflitos após aplicar. Ele segue sinalizado no cronograma.`;
}

/* Toasts do Aplicar (Figma 10121:46474). */
export const TOAST_SIMULACAO_APLICADA = "Simulação aplicada.";
export const TOAST_SIMULACAO_NAO_APLICADA = "Não foi possível aplicar a simulação.";
export const TOAST_ALTERACOES_REMOVIDAS = "Todas as alterações foram removidas.";
export const TOAST_ALTERACOES_REMOVIDAS_DETALHE = "Não há nada para aplicar.";
export const TOAST_SIMULACAO_NAO_APLICADA_DETALHE = "Nada foi perdido. Tente novamente.";
/** Falha ao reconsultar os dados antes de abrir o Aplicar (a simulação segue como está). */
export const TOAST_REVALIDACAO_FALHOU = "Não foi possível conferir os dados atuais.";

/* Toasts de sair e descartar (Figma 10121:46257 e 10121:46271). */
export const TOAST_SIMULACAO_DESCARTADA = "Simulação descartada.";
export const TOAST_SIMULACAO_DESCARTADA_DETALHE = "Nenhuma alteração foi gravada.";
export const TOAST_SAIU_DA_SIMULACAO = "Você saiu do Modo Simulação.";
export const TOAST_SIMULACAO_ARQUIVADA = "Simulação arquivada.";
export const TOAST_SIMULACAO_ARQUIVADA_DETALHE = "Nada foi gravado no cronograma.";

/** "As 3 alterações foram gravadas." (Figma 10121:46285). */
export function detalheSimulacaoAplicada(total: number): string {
  return total === 1 ? "A 1 alteração foi gravada." : `As ${total} alterações foram gravadas.`;
}

/** "Lucas Souza segue sinalizado." (Figma 10121:46301). */
export function detalheConflitosResiduais(recursos: string[]): string {
  return `${recursos.join(", ")} ${recursos.length === 1 ? "segue sinalizado" : "seguem sinalizados"}.`;
}

/** Lixeira de uma alteração no Aplicar (Figma 10121:46229). */
export function mensagemAlteracaoRemovida(recurso: string): string {
  return `Alteração de ${recurso} removida da simulação.`;
}

/** "Simulação aplicada com 1 conflito." / "... com 2 conflitos." (D-040). */
export function mensagemSimulacaoAplicadaComConflitos(total: number): string {
  return `Simulação aplicada com ${total} ${total === 1 ? "conflito" : "conflitos"}.`;
}

/* Sem texto literal no produto (decisão do time). */

export const TEXTO_APLICADA = "Gravada no sistema.";
export const TEXTO_NOVO_CONFLITO = "Gravada, mas gerou um novo conflito. Revise o cronograma.";
/** RN021: as datas da obra mudaram depois que a simulação começou. */
export const TEXTO_DATAS_ALTERADAS =
  "Gravada com aviso: as datas da obra mudaram depois que a simulação começou.";
/** RN022 */
export const TEXTO_OBRA_CANCELADA =
  "Não gravada: a obra foi cancelada depois que a simulação começou.";
/** RN019: outra aplicação já gravou o mesmo resultado (o último que aplica prevalece). */
export const TEXTO_JA_GRAVADA_POR_OUTRA = "Já estava gravada por outra aplicação.";
/** RN019: outra aplicação definiu outra equipe para a obra; não se substitui a equipe inteira. */
export const TEXTO_EQUIPE_DIFERENTE =
  "Não gravada: outra aplicação já definiu uma equipe diferente para esta obra.";
/** O conflito ignorado na simulação deixou de existir antes da aplicação. */
export const TEXTO_CONFLITO_INEXISTENTE = "O conflito já não existe.";
/** RN021: o período não é recortado; fora da etapa Obra, a alteração volta para ajuste. */
export const TEXTO_PERIODO_FORA_DA_OBRA =
  "Não gravada: as datas da obra mudaram e o período simulado ficou fora da obra.";
export const TEXTO_INDISPONIVEL =
  "Não gravada: o recurso escolhido não está mais disponível no período.";
export const TEXTO_SEM_PERMISSAO = "Não gravada: você não tem permissão para esta alteração.";
export const TEXTO_ALOCACAO_INEXISTENTE = "A alocação não existe mais.";
export const TEXTO_SEM_CONEXAO = "Sem conexão com o servidor. Use Retomar para continuar.";
export const TEXTO_JA_GRAVADA = "Já estava gravada (confirmado ao retomar).";

/** RN024: "Gravada com pendência: papel obrigatório sem profissional (Coordenador)." */
export function textoPapeisPendentes(nomes: readonly string[]): string {
  const papeis = nomes.length === 1 ? "papel obrigatório" : "papéis obrigatórios";
  return `Gravada com pendência: ${papeis} sem profissional (${nomes.join(", ")}).`;
}

/* Aplicar em andamento e concluído (Figma 10899:2309, 10899:2885, 10904:1573). */
export const TEXTO_APLICANDO_AVISO =
  "As alterações entram juntas. Se alguma falhar, nenhuma fica gravada e você vê o motivo no fim.";
export const TEXTO_APLICADA_SUCESSO = "Tudo certo. O cronograma já mostra as alterações.";
export const TEXTO_TENTAR_NOVAMENTE_AVISO = "Tentar novamente reenvia o conjunto inteiro.";

export function subtituloSimulacaoAplicada(total: number): string {
  return total === 1
    ? "A alteração foi gravada no sistema e o Modo Simulação foi encerrado."
    : `As ${total} alterações foram gravadas no sistema e o Modo Simulação foi encerrado.`;
}

/**
 * Título do Aplicar com falha parcial (Figma 10904:1573: "1 de 2 alterações aplicada"). O
 * substantivo concorda com o total; o particípio fica no singular para 0 e 1 e no plural de 2 em diante.
 */
export function tituloAplicacaoParcial(aplicadas: number, total: number): string {
  const alteracoes = total === 1 ? "alteração" : "alterações";
  const aplicada = aplicadas > 1 ? "aplicadas" : "aplicada";
  return `${aplicadas} de ${total} ${alteracoes} ${aplicada}`;
}

export const TITULO_SIMULACAO_APLICADA = "Simulação aplicada";
export const TITULO_APLICACAO_ATIVA = "Não foi possível aplicar";
export const TEXTO_APLICACAO_PARCIAL_SUBTITULO =
  "Algumas alterações não foram gravadas. O motivo está em cada cartão.";
export const TEXTO_APLICACAO_ATIVA_SUBTITULO =
  "Nada foi gravado. Ajuste o item com erro e aplique de novo.";

/** Tag de status de cada cartão no Aplicar (Figma 10899:2536, 10899:2542, 10899:2548). */
export const ROTULO_ESTADO_APLICACAO: Record<EstadoAplicacao, string> = {
  pendente: "Na fila",
  aplicando: "Aplicando…",
  aplicada: "Aplicada",
  "aplicada-com-aviso": "Aplicada com aviso",
  "nao-aplicada": "Não aplicada",
  descartada: "Descartada",
};

/** Botão do painel durante a gravação (Figma 10899:2473) e o de reenvio (Figma 10904:1573). */
export function rotuloAplicando(aplicadas: number, total: number): string {
  return `Aplicando… ${aplicadas} de ${total}`;
}

export function rotuloTentarNovamente(total: number): string {
  return `Tentar novamente (${total})`;
}

/** Título do painel direito do Aplicar: Resumo antes de confirmar, Progresso durante, Resultado depois. */
export const TITULO_PAINEL_APLICAR = {
  revisao: "Resumo",
  andamento: "Progresso",
  resultado: "Resultado",
} as const;

export function avisoFalhaDeSistema(total: number): string {
  return total === 1
    ? "1 alteração falhou por erro do sistema."
    : `${total} alterações falharam por erro do sistema.`;
}

/** Rótulo acessível da barra de progresso do Aplicar. */
export const ROTULO_PROGRESSO_APLICACAO = "Progresso da aplicação";
export const TEXTO_JA_JUSTIFICADO = "Conflitos novos já justificados na simulação.";
/**
 * Revalidação ao clicar em Aplicar (RN021 e RN022): o produto não traz texto para estes dois casos,
 * só para MS010 e MS011; os textos abaixo são nossos (extrapolação, a confirmar com o PO).
 * A MS011 (RN023) fica sem uso enquanto a alocação de fornecedor não tiver vínculo com o
 * Escopo Macro: o fornecedor que sumiu é tratado como alocação inexistente.
 */
export const AVISO_DATAS_ALTERADAS =
  "As datas da obra mudaram depois que a simulação começou. A alteração será aplicada com este aviso.";
export const AVISO_OBRA_CANCELADA =
  "A obra foi cancelada depois que a simulação começou. A alteração será descartada.";
export const TEXTO_JUSTIFICATIVA_OBRIGATORIA = "Informe a justificativa para aplicar a simulação.";

/** Máximo da justificativa geral (campo Justificativa: limite de 500 caracteres). */
export const JUSTIFICATIVA_GERAL_MAX = 500;

/* Modal de justificativa de novos conflitos (RN011; Figma 12041:410149). */
export const JUSTIFICATIVA_CONFLITOS_TITULO_AUSENCIA = "Novo conflito: profissional indisponível";
export const JUSTIFICATIVA_CONFLITOS_TITULO_UM = "Novo conflito de alocação";
export const JUSTIFICATIVA_CONFLITOS_TITULO_VARIOS = "Novos conflitos de alocação";
export const JUSTIFICATIVA_CONFLITOS_ORIENTACAO_UM =
  "Esta alteração da simulação cria um conflito. Justifique para seguir.";
export const JUSTIFICATIVA_CONFLITOS_ORIENTACAO_VARIOS =
  "Estas alterações da simulação criam conflitos. Justifique para seguir.";
export const JUSTIFICATIVA_CONFLITOS_AVISO_AUSENCIA =
  "O profissional não estará disponível durante parte do período da obra.";
export const JUSTIFICATIVA_CONFLITOS_VOLTAR = "Voltar";

export function justificativaConflitosAvisoAlocacao(codigoObraAtual: string): string {
  return `A alocação na obra ${codigoObraAtual} coincide com o período da nova obra.`;
}

const JUSTIFICATIVA_GUARDADA =
  "fica guardada na simulação e só é registrada quando você aplicar a simulação. Se descartar a simulação, ela também é descartada.";

export function justificativaConflitosEscopo(total: number): string {
  return total === 1
    ? `A justificativa ${JUSTIFICATIVA_GUARDADA}`
    : `A justificativa vale para os ${total} conflitos, ${JUSTIFICATIVA_GUARDADA}`;
}

/* Histórico de simulações (RF07). */
export const HISTORICO_SIMULACAO_TITULO = "Histórico de simulações";
export const HISTORICO_SIMULACAO_MENU = "Histórico de simulações";
export const HISTORICO_VER_SIMULACAO = "Ver simulação";
export const HISTORICO_DETALHE_TITULO = "Simulação";
export const HISTORICO_FORBIDDEN_MESSAGE =
  "Você não tem permissão para consultar o histórico de simulações.";
export const HISTORICO_ERROR_MESSAGE = "Não foi possível carregar o histórico de simulações.";
export const HISTORICO_DETALHE_ERROR_MESSAGE = "Não foi possível carregar esta simulação.";
export const HISTORICO_DESCARTADA_VAZIA = "Nenhuma alteração. A simulação foi descartada.";
export const HISTORICO_FILTRO_STATUS = "Status";
export const HISTORICO_COL_CRIADA = "Criada em";
export const HISTORICO_COL_USUARIO = "Realizada por";
export const HISTORICO_COL_STATUS = "Status";
export const HISTORICO_COL_ALTERACOES = "Alterações";
export const HISTORICO_COL_APLICADA = "Aplicada em";
export const HISTORICO_COL_JUSTIFICATIVA = "Justificativa";

const ROTULO_STATUS_HISTORICO: Record<string, string> = {
  APLICADA: "Aplicada",
  APLICADA_PARCIAL: "Aplicada parcial",
  DESCARTADA: "Descartada",
  ARQUIVADA: "Arquivada",
};

export function rotuloStatusHistorico(status: string): string {
  const chave = status.trim().toUpperCase();
  return ROTULO_STATUS_HISTORICO[chave] ?? status;
}
